/**
 * Font Mapping Utilities
 *
 * Provides functions for mapping detected fonts to Google Fonts alternatives
 * and generating Google Fonts embed URLs.
 *
 * @module fonts
 *
 * @example
 * ```typescript
 * import { mapFontToGoogleFonts, generateGoogleFontsUrl } from '@marketing-optimizer/themes/fonts';
 *
 * // Map a proprietary font to a Google Fonts alternative
 * const fontStack = mapFontToGoogleFonts('Proxima Nova');
 * // => '"Montserrat", sans-serif'
 *
 * // Generate embed URL for multiple fonts
 * const url = generateGoogleFontsUrl(['Montserrat', 'Playfair Display']);
 * // => 'https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700&family=Playfair+Display:wght@400;500;600;700&display=swap'
 * ```
 */

import {
  ALL_FONTS,
  DEFAULT_FALLBACKS,
  FONT_FALLBACK_MAP,
  GOOGLE_FONTS,
  type FontCategory,
  type FontEntry,
} from './font-database';

export * from './font-database';

/**
 * Result of font resolution
 */
export interface FontResolution {
  /** The resolved Google Fonts family name */
  googleFontsName: string;
  /** The Google Fonts ID for URL generation */
  googleFontsId: string;
  /** Full CSS font-family stack with fallbacks */
  fontStack: string;
  /** Whether this was a direct match or a fallback */
  isDirectMatch: boolean;
  /** Original font name that was requested */
  originalFont: string;
}

/**
 * Normalizes a font name for lookup
 *
 * Handles common variations like:
 * - Case differences: "proxima nova" → "Proxima Nova"
 * - Dashes/underscores: "proxima-nova" → "Proxima Nova"
 * - Extra spaces: "Proxima  Nova" → "Proxima Nova"
 */
export function normalizeFontName(fontName: string): string {
  return fontName
    .replace(/[-_]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

/**
 * Finds a font entry in the database by name or alias
 */
export function findFontEntry(fontName: string): FontEntry | undefined {
  const normalized = normalizeFontName(fontName);

  // Check exact name match
  let entry = ALL_FONTS.find(
    (f) => normalizeFontName(f.name) === normalized
  );
  if (entry) return entry;

  // Check aliases
  entry = ALL_FONTS.find((f) =>
    f.aliases.some((alias) => normalizeFontName(alias) === normalized)
  );
  if (entry) return entry;

  // Check partial match (font name contains or is contained)
  entry = ALL_FONTS.find(
    (f) =>
      normalizeFontName(f.name).includes(normalized) ||
      normalized.includes(normalizeFontName(f.name))
  );

  return entry;
}

/**
 * Checks if a font is directly available on Google Fonts
 */
export function isGoogleFont(fontName: string): boolean {
  const normalized = normalizeFontName(fontName);
  return GOOGLE_FONTS.some(
    (f) =>
      normalizeFontName(f.name) === normalized ||
      f.aliases.some((alias) => normalizeFontName(alias) === normalized)
  );
}

/**
 * Gets a Google Font entry by name
 */
export function getGoogleFontEntry(fontName: string): FontEntry | undefined {
  const normalized = normalizeFontName(fontName);
  return GOOGLE_FONTS.find(
    (f) =>
      normalizeFontName(f.name) === normalized ||
      f.aliases.some((alias) => normalizeFontName(alias) === normalized)
  );
}

/**
 * Infers the category of a font from its name
 *
 * Used when we can't find the font in our database but need to pick a fallback.
 */
export function inferFontCategory(fontName: string): FontCategory {
  const lower = fontName.toLowerCase();

  // Serif indicators
  if (
    lower.includes('serif') ||
    lower.includes('times') ||
    lower.includes('georgia') ||
    lower.includes('garamond') ||
    lower.includes('bodoni') ||
    lower.includes('didot') ||
    lower.includes('baskerville') ||
    lower.includes('caslon') ||
    lower.includes('palatino')
  ) {
    return 'serif';
  }

  // Monospace indicators
  if (
    lower.includes('mono') ||
    lower.includes('code') ||
    lower.includes('courier') ||
    lower.includes('consolas')
  ) {
    return 'monospace';
  }

  // Handwriting/script indicators
  if (
    lower.includes('script') ||
    lower.includes('hand') ||
    lower.includes('cursive') ||
    lower.includes('brush')
  ) {
    return 'handwriting';
  }

  // Display indicators
  if (
    lower.includes('display') ||
    lower.includes('headline') ||
    lower.includes('poster')
  ) {
    return 'display';
  }

  // Default to sans-serif
  return 'sans-serif';
}

/**
 * Maps a font name to a Google Fonts alternative
 *
 * If the font is already a Google Font, returns it directly.
 * If it's a proprietary font, finds the best matching alternative.
 *
 * @param fontName - The font name to map
 * @returns FontResolution with the resolved font details
 */
export function mapFontToGoogleFonts(fontName: string): FontResolution {
  const normalized = normalizeFontName(fontName);

  // Check if it's already a Google Font
  const googleFont = getGoogleFontEntry(fontName);
  if (googleFont) {
    return {
      googleFontsName: googleFont.name,
      googleFontsId: googleFont.googleFontsId!,
      fontStack: `"${googleFont.name}", ${googleFont.category}`,
      isDirectMatch: true,
      originalFont: fontName,
    };
  }

  // Check the fallback map
  const fallbackName =
    FONT_FALLBACK_MAP[normalized] ||
    FONT_FALLBACK_MAP[fontName] ||
    Object.entries(FONT_FALLBACK_MAP).find(([key]) =>
      normalized.includes(normalizeFontName(key))
    )?.[1];

  if (fallbackName) {
    const fallbackFont = getGoogleFontEntry(fallbackName);
    if (fallbackFont) {
      return {
        googleFontsName: fallbackFont.name,
        googleFontsId: fallbackFont.googleFontsId!,
        fontStack: `"${fallbackFont.name}", ${fallbackFont.category}`,
        isDirectMatch: false,
        originalFont: fontName,
      };
    }
  }

  // Find by category
  const entry = findFontEntry(fontName);
  if (entry) {
    const category = entry.category;
    const fallback = DEFAULT_FALLBACKS[category];
    const fallbackEntry = getGoogleFontEntry(fallback);
    if (fallbackEntry) {
      return {
        googleFontsName: fallbackEntry.name,
        googleFontsId: fallbackEntry.googleFontsId!,
        fontStack: `"${fallbackEntry.name}", ${category}`,
        isDirectMatch: false,
        originalFont: fontName,
      };
    }
  }

  // Last resort: font not in our database at all.
  // Assume it's a valid Google Font and pass through directly.
  // If it doesn't exist on Google Fonts, the CSS category fallback handles it gracefully.
  const category = inferFontCategory(fontName);
  const googleFontsId = normalized.replace(/ /g, '+');

  return {
    googleFontsName: normalized,
    googleFontsId,
    fontStack: `"${normalized}", ${category}`,
    isDirectMatch: false,
    originalFont: fontName,
  };
}

/**
 * Generates a Google Fonts embed URL for multiple fonts
 *
 * @param fonts - Array of font names (Google Fonts names or will be mapped)
 * @param weights - Array of font weights to include (default: [400, 500, 600, 700])
 * @returns Google Fonts CSS URL
 *
 * @example
 * ```typescript
 * generateGoogleFontsUrl(['Montserrat', 'Playfair Display'])
 * // => 'https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700&family=Playfair+Display:wght@400;500;600;700&display=swap'
 * ```
 */
export function generateGoogleFontsUrl(
  fonts: string[],
  weights: number[] = [400, 500, 600, 700]
): string {
  const weightStr = weights.join(';');
  const seen = new Set<string>();

  const families = fonts
    .map((font) => {
      const resolution = mapFontToGoogleFonts(font);
      // Deduplicate by Google Fonts ID
      if (seen.has(resolution.googleFontsId)) {
        return null;
      }
      seen.add(resolution.googleFontsId);
      return `family=${resolution.googleFontsId}:wght@${weightStr}`;
    })
    .filter(Boolean)
    .join('&');

  if (!families) {
    return '';
  }

  return `https://fonts.googleapis.com/css2?${families}&display=swap`;
}

/**
 * Resolves fonts for a provider and returns all necessary information
 *
 * @param bodyFont - Detected body font name
 * @param headingFont - Detected heading font name (optional, defaults to body)
 * @returns Object with resolved fonts and Google Fonts URL
 */
export function resolveProviderFonts(
  bodyFont: string,
  headingFont?: string
): {
  body: FontResolution;
  heading: FontResolution;
  googleFontsUrl: string;
} {
  const body = mapFontToGoogleFonts(bodyFont);
  const heading = mapFontToGoogleFonts(headingFont || bodyFont);

  const fontsToLoad =
    body.googleFontsId === heading.googleFontsId
      ? [body.googleFontsName]
      : [body.googleFontsName, heading.googleFontsName];

  return {
    body,
    heading,
    googleFontsUrl: generateGoogleFontsUrl(fontsToLoad),
  };
}
